import {parse as parseCsv, unparse as unparseCsv} from "papaparse/papaparse.min";
import { downloadCsv } from "../utils";



/**
 * Util compartilhado para geração e download de CSV de erros
 * 
 * Esta util permite adicionar colunas de status e motivo de erro
 * ao CSV original do painel inserido, mantendo todas as colunas originais.
 * 
 * // 1. Validar o CSV e obter resultados
 * // 2. Gerar CSV com erros
 * // 3. Fazer download do CSV
 */

/**
 * Gera CSV de erros adicionando colunas de status e motivo de erro
 * 
 * @param {string} originalCsvText - Texto completo do CSV original
 * @param {Array} validationResults - Array com resultados da validação
 *   Cada item deve conter: { rowNumber, isValid, errors: [] }
 * @param {Array} originalHeaders - Headers originais do CSV (opcional)
 * @param {Object} options - Opções adicionais
 * @param {string} options.statusColumnName - Nome da coluna de status (padrão: "Status")
 * @param {string} options.errorColumnName - Nome da coluna de motivo (padrão: "Motivo(s) do(s) erro(s)")
 * @returns {string} CSV com colunas de erro adicionadas
 */
export const generateErrorCsv = (
  originalCsvText,
  validationResults,
  originalHeaders = [],
  options = {}
) => {
  try {
    const {
      statusColumnName = "Status",
      errorColumnName = "Motivo(s) do(s) erro(s)"
    } = options;

    const parsedCsv = parseCsv(originalCsvText, {
      delimiter: ";",
      skipEmptyLines: true,
    });

    if (parsedCsv.errors && parsedCsv.errors.length > 0) {
      throw new Error(parsedCsv.errors[0].message);
    }

    const rows = parsedCsv.data;

    if (!rows.length) {
      return "";
    }

    const headers = rows[0].map((header) => `${header ?? ""}`.trim());

    const hasStatusColumn = headers.some(h =>
      h.toLowerCase() === statusColumnName.toLowerCase()
    );
    const hasErrorColumn = headers.some(h =>
      h.toLowerCase() === errorColumnName.toLowerCase()
    );

    const newHeaders = [...headers];
    if (!hasStatusColumn) {
      newHeaders.push(statusColumnName);
    }
    if (!hasErrorColumn) {
      newHeaders.push(errorColumnName);
    }

    const statusIndex = hasStatusColumn
      ? headers.findIndex(h => h.toLowerCase() === statusColumnName.toLowerCase())
      : -1;
    const errorIndex = hasErrorColumn
      ? headers.findIndex(h => h.toLowerCase() === errorColumnName.toLowerCase())
      : -1;

    const dataRows = rows.slice(1).map((row, index) => {
      const rowData = row.map((cell) => `${cell ?? ""}`.trim());
      const validation = validationResults[index] || { isValid: false, errors: [] };

      const status = validation.isValid ? "OK" : "ERRO";
      
      const motivo = validation.errors && validation.errors.length > 0
        ? validation.errors.join(" | ")
        : "";

      if (hasStatusColumn && statusIndex >= 0) {
        rowData[statusIndex] = status;
      } else {
        rowData.push(status);
      }

      if (hasErrorColumn && errorIndex >= 0) {
        rowData[errorIndex] = motivo;
      } else {
        rowData.push(motivo);
      }

      return rowData;
    });

    return unparseCsv([newHeaders, ...dataRows], {
      delimiter: ";",
    });
  } catch (error) {
    console.error("Erro ao gerar CSV de erros:", error);
    throw new Error(`Erro ao gerar CSV de erros: ${error.message}`);
  }
};

/**
 * Faz download do CSV de erros
 * 
 * @param {string} errorCsv - Conteúdo do CSV com erros
 * @param {string} filename - Nome do arquivo (sem extensão)
 */
export const downloadErrorCsvFile = (errorCsv, filename = "erros-validacao") => {
  if (!errorCsv || typeof errorCsv !== "string" || errorCsv.trim() === "") {
    throw new Error("CSV inválido ou vazio");
  }

  const cleanFilename = filename.replace(/\.csv(?=-|$)/gi, "");

  downloadCsv(errorCsv, cleanFilename);
};

